/**
 * Shared model for "is the data on this screen current?".
 *
 * `AGENTS.md`: "Connectivity status is only a hint; backend reachability and
 * synchronization require real API outcomes and acknowledgements." So the
 * browser's `navigator.onLine` value is recorded as a hint and nothing more.
 * `reachability` moves only when a real request to the BurgePOS API succeeds or
 * fails, which is why it starts at `unverified` rather than at `reachable`.
 *
 * PRD section 6.6 requires that "a stale connection reads as stale, not as zero
 * sales". This module exists so every management screen says that the same way.
 */

export type Reachability = "unverified" | "reachable" | "unreachable";

export type ConnectionState = {
  reachability: Reachability;
  /** Set only by a successful API response. */
  lastVerifiedAt: Date | null;
  /** `navigator.onLine`. A hint about the local network adapter, not about BurgePOS. */
  browserOnlineHint: boolean;
};

export type ConnectionEvent =
  | { type: "api-request-succeeded"; at: Date }
  | { type: "api-request-failed"; at: Date }
  | { type: "browser-online-hint-changed"; online: boolean };

export function initialConnectionState(browserOnlineHint: boolean): ConnectionState {
  return { browserOnlineHint, lastVerifiedAt: null, reachability: "unverified" };
}

export function reduceConnectionState(
  state: ConnectionState,
  event: ConnectionEvent,
): ConnectionState {
  switch (event.type) {
    case "api-request-succeeded":
      return { ...state, lastVerifiedAt: event.at, reachability: "reachable" };
    case "api-request-failed":
      // A failure proves BurgePOS is unreachable. It does not invalidate the
      // last time data *was* confirmed, which is what makes staleness readable.
      return { ...state, reachability: "unreachable" };
    case "browser-online-hint-changed":
      // Deliberately does not touch `reachability`. The adapter being up is not
      // evidence that BurgePOS answered.
      return { ...state, browserOnlineHint: event.online };
  }
}

/**
 * PRD goal G3 promises the dashboard reflects a completed shift close "within 5
 * minutes of the till going back online", so five minutes is the point at which
 * confirmed data stops being presentable as current.
 */
export const STALE_AFTER_MS = 5 * 60 * 1000;

/** The v1 business time zone (`AGENTS.md`). Never a fixed offset, never "CAT". */
export const BUSINESS_TIME_ZONE = "Africa/Lusaka";

export type ConnectionNotice = {
  /** `null` when there is nothing degraded to report and the banner stays hidden. */
  tone: "info" | "warning" | "danger" | null;
  title: string;
  description: string;
};

export function formatBusinessTime(instant: Date): string {
  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: BUSINESS_TIME_ZONE,
    timeZoneName: "short",
  }).format(instant);
}

export function isStale(state: ConnectionState, now: Date): boolean {
  if (state.lastVerifiedAt === null) {
    return true;
  }
  return now.getTime() - state.lastVerifiedAt.getTime() >= STALE_AFTER_MS;
}

/**
 * Turns the state into the one persistent banner the shell shows. Degraded
 * connectivity is a persistent banner rather than a toast because it stays true
 * until it is fixed (`AGENTS.md` messaging rules).
 */
export function describeConnection(state: ConnectionState, now: Date): ConnectionNotice {
  // Nothing has been confirmed or refused yet: no banner. Only data screens
  // report outcomes, so "unverified" also covers the sign-in page and every
  // screen without figures, where "no server has answered" was untrue
  // (Musweu's Windows run, 12 October 2026). Data screens show their own
  // loading and empty states until an answer arrives.
  if (state.reachability === "unverified") {
    return { description: "", title: "", tone: null };
  }

  if (state.reachability === "unreachable") {
    const confirmed = state.lastVerifiedAt
      ? `The last figures BurgePOS confirmed were from ${formatBusinessTime(state.lastVerifiedAt)}. Treat them as out of date, not as zero.`
      : "Nothing on screen has been confirmed by BurgePOS. Treat it as unknown, not as zero.";
    return {
      description: `${confirmed} ${
        state.browserOnlineHint
          ? "This device reports a working network connection, so the problem is between here and BurgePOS."
          : "This device reports no network connection."
      }`,
      title: "BurgePOS is not responding",
      tone: "danger",
    };
  }

  if (isStale(state, now)) {
    return {
      description: `The last confirmed update was at ${formatBusinessTime(state.lastVerifiedAt as Date)}. Newer sales or stock movements may not be shown yet.`,
      title: "Showing data that is out of date",
      tone: "warning",
    };
  }

  return { description: "", title: "", tone: null };
}
